import { useEffect, useRef, useState } from 'react'

interface Props {
  min: number
  max: number
}

function lifestyleReframe(midKr: number): string {
  if (midKr < 1500) return 'Det er en festivalbillet 🎶'
  if (midKr < 5000) return 'Det er en weekendstur til Berlin ✈️'
  if (midKr < 8000) return 'Det er en interrail-tur 🚆'
  return 'Det er en lille ferie hvert år 🏖️'
}

const fmt = (n: number) => Math.round(n).toLocaleString('da-DK')

/**
 * Animeret count-up fra 0 til target over `duration` ms med ease-out.
 * Returnerer aktuel værdi så caller kan rendere den.
 */
function useCountUp(target: number, duration = 1100, startDelay = 250): number {
  const [v, setV] = useState(0)
  const rafRef = useRef<number | null>(null)
  useEffect(() => {
    setV(0)
    let start: number | null = null
    let startTimeout: number | null = null
    const tick = (now: number) => {
      if (start === null) start = now
      const elapsed = now - start
      const t = Math.min(1, elapsed / duration)
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3)
      setV(eased * target)
      if (t < 1) rafRef.current = requestAnimationFrame(tick)
    }
    startTimeout = window.setTimeout(() => {
      rafRef.current = requestAnimationFrame(tick)
    }, startDelay)
    return () => {
      if (startTimeout !== null) clearTimeout(startTimeout)
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    }
  }, [target, duration, startDelay])
  return v
}

export function SavingsCard({ min, max }: Props) {
  const valMin = useCountUp(min, 1100, 250)
  const valMax = useCountUp(max, 1100, 350)
  const mid = (min + max) / 2

  return (
    <div className="savings-card" role="figure" aria-label={`Du kan spare mellem ${min} og ${max} kr. om året`}>
      <div className="savings-eyebrow">💰 Du kan potentielt spare</div>
      <div className="savings-amount">
        <span className="savings-num">{fmt(valMin)}</span>
        <span className="savings-dash">–</span>
        <span className="savings-num">{fmt(valMax)}</span>
        <span className="savings-unit">kr.</span>
      </div>
      <div className="savings-period">om året</div>
      <div className="savings-bar">
        <div className="savings-bar-fill" />
      </div>
      <div className="savings-reframe">{lifestyleReframe(mid)}</div>
    </div>
  )
}
